import { DateTime } from 'luxon';

type StringDateTimeMethod = (value: string) => DateTime<boolean>;

/**
 * Represents methods on the {@link DateTime} class for parsing date strings in common forms that
 * are typically used by computers.  When attempting to infer the {@link DateTime} associated
 * with a given string, these forms will checked before token-based parsing.
 */
const CommonDateParsers: StringDateTimeMethod[] = [
  value => DateTime.fromISO(value),
  value => DateTime.fromHTTP(value),
  value => DateTime.fromSQL(value),
  value => DateTime.fromRFC2822(value),
];

/**
 * Tokens that represent expressions of the current month, as an integer that is either padded
 * (e.g. 03) or not padded (e.g. 3).
 *
 * @see https://moment.github.io/luxon/#/parsing?id=table-of-tokens
 */
const MonthTokens = ['MM', 'M'];

/**
 * Tokens that represent expressions of the current month, as a string name (e.g. Aug or August).
 *
 * @see https://moment.github.io/luxon/#/parsing?id=table-of-tokens
 */
const MonthNameTokens = ['MMM', 'MMMM'];

/**
 * Tokens that represent expressions of the current day of the month, as an integer that is either
 * padded (e.g. 03) or not padded (e.g. 3).
 *
 * @see https://moment.github.io/luxon/#/parsing?id=table-of-tokens
 */
const DayTokens = ['dd', 'd'];

/**
 * Tokens that represent expressions of the current year, as an integer that is either 2 characters
 * (e.g. 24) or 4 characters (e.g. 2024).
 *
 * @see https://moment.github.io/luxon/#/parsing?id=table-of-tokens
 */
const YearTokens = ['yy', 'yyyy'];

/**
 * A representation of various ways that tokens can be used to format a {@link Date}.  When
 * attempting to infer the {@link DateTime} for a given {@link string}, an attempt to parse the
 * {@link string} with each of these tokens will be attempted until a match is found.
 */
const DateTokenFormats: string[] = [
  ...MonthTokens.flatMap(month =>
    DayTokens.flatMap(day => YearTokens.map(year => `${month}/${day}/${year}`)),
  ),
  ...MonthNameTokens.flatMap(month =>
    DayTokens.flatMap(day => YearTokens.map(year => `${month} ${day} ${year}`)),
  ),
  'DD',
  'DDD',
  'D',
];

/**
 * Standardizes a value for date inference by removing duplicate white space characters and all
 * non-alphanumeric characters that are not either '/' or '-'.
 */
const standardizeValueForInference = (value: string) =>
  value
    .replace(/[^a-zA-Z0-9/-\s]/g, '')
    .replace(/\s+/g, ' ')
    .trim();

/**
 * Attempts to parse the {@link DateTime} instance associated with the provided {@link string},
 * parsing with various token formats until a match is found.
 *
 * This method can be used to safely determine whether or not a given {@link string} (usually
 * entered by a user and not a machine) represents a valid {@link DateTime} instance.  It is safer
 * and stricter than simply doing the following:
 *
 * >>> const date = new Date('...')
 *
 * This is because the {@link Date} constructor will convert a lot of string values that are not
 * valid representations of a {@link Date} into a valid {@link Date}, which can lead to unexpected
 * results.
 *
 * Note: This method is only exported for use by the {@link safelyConvertDate} method, and should
 * not be used outside of this module.  Instead, the {@link safelyConvertDate} method should
 * be used directly.
 *
 * @param {string} value
 *   The value to infer the {@link DateTime} from.
 *
 * @returns {DateTime<true> | undefined}
 *   Either a valid, {@link DateTime} instance, if the provided {@link string} could be parsed,
 *   or undefined if it could not.
 */
export const inferDateTime = (value: string): DateTime<true> | undefined => {
  for (const method of CommonDateParsers) {
    const result = method(value.trim());
    if (result.isValid) {
      return result;
    }
  }
  for (const formatString of DateTokenFormats) {
    const matched = DateTime.fromFormat(standardizeValueForInference(value), formatString);
    if (matched.isValid) {
      return matched;
    }
  }
  return undefined;
};
